import { Flex, FlexProps, Icon, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { Card } from "src/components/Layout/Card";
import { isNonEmptyNode, isNullOrUndefined } from "src/services/predicates";
import { Glossary } from "../Text/Glossary";

type CollapsibleVariant = {
  variant?: "collapsible";
  collapse: boolean;
};

type NormalVariant = {
  variant?: "normal" | undefined;
};

type Props = {
  alert?: React.ReactNode;
  switches?: React.ReactNode;
  title: string | React.ReactNode;
  description?: string | null;
  tag?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  icon?: IconType;
} & (CollapsibleVariant | NormalVariant);

const CONTAINER_PADDING_X = 6;
const CONTAINER_PADDING_Y = 4;

const PADDING = {
  paddingX: CONTAINER_PADDING_X,
  paddingY: CONTAINER_PADDING_Y,
};

export const AdminCard: React.FC<Props> = ({
  alert,
  tag,
  title,
  description,
  actions,
  disabled,
  switches,
  children,
  icon,
  ...props
}) => {
  const isCollapsible = props.variant === "collapsible";
  const sectionPadding = isCollapsible ? PADDING : undefined;

  let content: React.ReactNode = null;
  if (isCollapsible) {
    if (!props.collapse) {
      if (isNonEmptyNode(children)) {
        content = (
          <Flex
            direction="column"
            borderTop="1px solid"
            borderColor="chakra-border-color"
            {...PADDING}
          >
            {children}
          </Flex>
        );
      }
    }
  } else if (isNonEmptyNode(children)) {
    content = <Flex direction="column">{children}</Flex>;
  }

  return (
    <Card as={Flex} showBorder padding="0" width="100%" flexDirection="column">
      {alert && <Flex>{alert}</Flex>}
      <Flex
        direction="column"
        gap={isCollapsible ? undefined : "4"}
        padding={isCollapsible ? undefined : "6"}
        paddingTop={isCollapsible ? undefined : "4"}
        sx={{ containerType: "inline-size" }}
      >
        {isNonEmptyNode(tag) ? (
          <Flex grow="1" gap="2" {...sectionPadding}>
            {tag}
          </Flex>
        ) : null}

        <Flex
          {...sectionPadding}
          alignItems="flex-start"
          justifyContent="space-between"
          width="100%"
          gap="3"
          sx={{
            "@container (width < 600px)": {
              flexDirection: "column",
            },
          }}
        >
          <Flex alignItems="center" gap="4">
            {isNullOrUndefined(icon) ? null : <Icon as={icon} boxSize="6" />}
            <Flex direction="column">
              <Flex
                fontSize="lg"
                color={disabled ? "gray.500" : undefined}
                as={disabled ? "i" : undefined}
              >
                {typeof title === "string" ? (
                  <Text>
                    <Glossary>{title}</Glossary>
                  </Text>
                ) : (
                  title
                )}
              </Flex>
              {description && (
                <Text color="blackAlpha.600" fontWeight="400">
                  {description}
                </Text>
              )}
            </Flex>
          </Flex>
          {actions || switches ? (
            <Flex
              direction="row"
              gap={4}
              alignItems="center"
              flexWrap="wrap"
              flexShrink="0"
              alignSelf="flex-end"
            >
              {switches && (
                <Flex gap="4" mr="2">
                  {switches}
                </Flex>
              )}
              {actions}
            </Flex>
          ) : null}
        </Flex>
        {content}
      </Flex>
    </Card>
  );
};

type FormButtonProps = FlexProps & React.PropsWithChildren;
export const AdminCardFormButtons: React.FC<FormButtonProps> = ({
  children,
  ...props
}) => {
  return (
    <Flex
      marginTop={CONTAINER_PADDING_Y}
      paddingTop={CONTAINER_PADDING_Y}
      paddingX={CONTAINER_PADDING_X}
      justifyContent="flex-end"
      alignItems="center"
      gap="2"
      borderTop="1px solid"
      borderColor="chakra-border-color"
      width={`calc(100% + (var(--chakra-space-${CONTAINER_PADDING_X}) * 2))`}
      marginLeft={`calc(-1 * var(--chakra-space-${CONTAINER_PADDING_X}))`}
      {...props}
    >
      {children}
    </Flex>
  );
};
